import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { FileText as A1, Search as A2, CheckCircle as A3, AlertTriangle as A4, Sparkles as A5, Download as A6 } from 'lucide-react';

import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  FileText, Upload, Target, CheckCircle, XCircle, AlertTriangle,
  Zap, TrendingUp, Download, RefreshCw, Sparkles, ChevronRight,
  ChevronDown, ArrowRight, Briefcase, GraduationCap, Star, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Score color helper
const getScoreColor = (score) => {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-yellow-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
};

const getScoreBgColor = (score) => {
  if (score >= 80) return 'bg-emerald-500/20 border-emerald-500/30';
  if (score >= 60) return 'bg-yellow-500/20 border-yellow-500/30';
  if (score >= 40) return 'bg-orange-500/20 border-orange-500/30';
  return 'bg-red-500/20 border-red-500/30';
};

const getScoreGradient = (score) => {
  if (score >= 80) return 'from-emerald-600 to-green-600';
  if (score >= 60) return 'from-yellow-600 to-amber-600';
  if (score >= 40) return 'from-orange-600 to-red-600';
  return 'from-red-600 to-rose-600';
};

// Circular Score Display
const ScoreCircle = ({ score, size = 'large', label }) => {
  const radius = size === 'large' ? 70 : 35;
  const stroke = size === 'large' ? 8 : 4;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${size === 'large' ? 'w-40 h-40' : 'w-20 h-20'}`}>
        <svg className="transform -rotate-90 w-full h-full">
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            className="fill-none stroke-slate-700"
            strokeWidth={stroke}
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            className={`fill-none ${score >= 80 ? 'stroke-emerald-500' : score >= 60 ? 'stroke-yellow-500' : score >= 40 ? 'stroke-orange-500' : 'stroke-red-500'}`}
            strokeWidth={stroke}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s ease-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${size === 'large' ? 'text-4xl' : 'text-xl'} font-bold ${getScoreColor(score)}`}>
            {score}%
          </span>
          {size === 'large' && (
            <span className="text-slate-300 text-sm">ATS Score</span>
          )}
        </div>
      </div>
      {label && <span className="text-slate-300 text-xs mt-2">{label}</span>}
    </div>
  );
};

// Score Breakdown Bar
const ScoreBar = ({ label, score, icon: Icon }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-sm">
      <span className="flex items-center gap-2 text-slate-300">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </span>
      <span className={`font-semibold ${getScoreColor(score)}`}>{score}%</span>
    </div>
    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${score}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className={`h-full rounded-full ${score >= 80 ? 'bg-emerald-500' : score >= 60 ? 'bg-yellow-500' : score >= 40 ? 'bg-orange-500' : 'bg-red-500'}`}
      />
    </div>
  </div>
);

// Keyword Chip
const KeywordChip = ({ keyword, found }) => (
  <span className={`px-2 py-1 rounded-full text-xs ${
    found 
      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
      : 'bg-red-500/20 text-red-400 border border-red-500/30'
  }`}>
    {found ? '✓' : '✗'} {keyword}
  </span>
);

// Improvement Card
const ImprovementCard = ({ suggestion }) => {
  const [expanded, setExpanded] = useState(false);
  
  const priorityColors = {
    High: 'border-red-500/50 bg-red-500/10',
    Medium: 'border-yellow-500/50 bg-yellow-500/10',
    Low: 'border-blue-500/50 bg-blue-500/10'
  };
  
  return (
    <div className={`border rounded-lg p-4 ${priorityColors[suggestion.priority] || priorityColors.Medium}`}>
      <div className="flex items-start justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              suggestion.priority === 'High' ? 'bg-red-500/30 text-red-400' :
              suggestion.priority === 'Medium' ? 'bg-yellow-500/30 text-yellow-400' :
              'bg-blue-500/30 text-blue-400'
            }`}>
              {suggestion.priority}
            </span>
            <span className="text-sm text-slate-300">{suggestion.category}</span>
          </div>
          <p className="text-white font-medium">{suggestion.impact}</p>
        </div>
        {expanded ? <ChevronDown className="w-5 h-5 text-slate-300" /> : <ChevronRight className="w-5 h-5 text-slate-300" />}
      </div>
      
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="mt-3 pt-3 border-t border-slate-700/50"
          >
            {suggestion.current && (
              <div className="mb-2">
                <span className="text-xs text-slate-300 uppercase">Current:</span>
                <p className="text-slate-300 text-sm mt-1">{suggestion.current}</p>
              </div>
            )}
            <div>
              <span className="text-xs text-slate-300 uppercase">Suggested:</span>
              <p className="text-emerald-400 text-sm mt-1">{suggestion.suggested}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function ResumeATSPage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const [step, setStep] = useState(1); // 1: upload, 2: job desc, 3: results
  const [loading, setLoading] = useState(false);
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [savedResume, setSavedResume] = useState(null);
  const [locked, setLocked] = useState(false);
  const fileInputRef = useRef(null);
  
  // Load saved resume on mount
  useEffect(() => {
    loadSavedResume();
  }, []);
  
  const loadSavedResume = async () => {
    try {
      const response = await axios.get(`${API}/resume/saved`, {
        ...getAuthHeader(),
        withCredentials: true
      });
      if (response.data.success && response.data.data) {
        setSavedResume(response.data.data);
      }
    } catch (error) {
      if (error?.response?.status === 403) setLocked(true);
      console.log('No saved resume found');
    }
  };

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="resume-arrival"
        eyebrow="Résumé Analyzer"
        title="Get past the software first."
        subtitle="Most résumés are filtered by a machine before a person sees them. This checks yours against the job you actually want and tells you exactly what to fix."
        accent="#22d3ee"
        second="#a78bfa"
        ctaLabel="Check my résumé"
        narration={"This is the Résumé Analyzer. Before a human reads your résumé, software decides whether it reaches them, and it is looking for specific things. Paste the job you want and your résumé, and you will see what matched, what is missing, what formatting will cause problems, and the exact lines to change. No rewriting your history, just making the truth readable to a machine and then to a person."}
        facts={[
          { icon: A2, head: "Checked against the job", sub: "Not scored in the abstract" },
          { icon: A4, head: "Problems named", sub: "Formatting and wording that gets filtered out" },
          { icon: A3, head: "Exact fixes", sub: "The lines to change, not vague advice" }
        ]}
        modules={[
          { icon: A1, label: "Upload or paste", line: "Your résumé, in any usual format", hue: "#22d3ee" },
          { icon: A2, label: "Match score", line: "How well it lines up with the posting", hue: "#a78bfa" },
          { icon: A4, label: "Missing keywords", line: "What the posting expects to see", hue: "#fbbf24" },
          { icon: A3, label: "Formatting check", line: "What breaks in the software", hue: "#34d399" },
          { icon: A5, label: "Better lines", line: "Stronger wording for what you already did", hue: "#f472b6" },
          { icon: A6, label: "Export", line: "A clean version ready to send", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  if (locked) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="ats-locked">
        <div className="max-w-lg text-center rounded-2xl border border-amber-400/30 bg-amber-400/[0.07] backdrop-blur-xl p-10">
          <Lock className="w-8 h-8 text-amber-300 mx-auto" />
          <h1 className="font-outfit text-2xl font-semibold text-white mt-5">This tool is on the adult plans</h1>
          <p className="text-base text-slate-300/90 mt-4">
            The Resume ATS Analyzer is built for job seekers 18 and over. It's included with the
            Young Adult and Professional plans.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/pricing" className="btn-primary text-sm px-6 py-2.5" data-testid="ats-locked-pricing">See plans</Link>
            <Link to="/app/careers" className="text-sm px-6 py-2.5 rounded-full border border-white/25 text-white hover:bg-white/[0.06] transition-colors duration-200" data-testid="ats-locked-careers">
              Explore careers instead
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/resume/upload`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });
      
      if (response.data.success) {
        setResumeText(response.data.text);
        toast.success(`Resume uploaded! (${response.data.character_count} characters extracted)`);
        setStep(2);
      }
    } catch (error) {
      showFriendlyError(error, { fallback: 'Failed to upload resume' });
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasteResume = async () => {
    if (resumeText.length < 100) {
      toast.error('Please enter at least 100 characters');
      return;
    }
    
    // Save pasted resume
    try {
      await axios.post(`${API}/resume/save-text`, {
        resume_text: resumeText
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      setStep(2);
    } catch (error) {
      // Continue anyway
      setStep(2);
    }
  };
  
  const useSavedResume = () => {
    if (savedResume) {
      setResumeText(savedResume.text);
      setStep(2);
    }
  };
  
  const analyzeResume = async () => {
    if (!resumeText || !jobDescription) {
      toast.error('Please provide both resume and job description');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/resume/analyze`, {
        resume_text: resumeText,
        job_description: jobDescription
      }, {
        ...getAuthHeader(),
        withCredentials: true
      });
      
      if (response.data.success) {
        setAnalysis(response.data.analysis);
        setStep(3);
        toast.success(`Analysis complete! Used ${response.data.credits_used} credits`);
      } else {
        toast.error('Analysis failed - please try again');
      }
    } catch (error) {
      showFriendlyError(error, { fallback: 'Analysis failed' });
    } finally {
      setLoading(false);
    }
  };
  
  const downloadReport = () => {
    if (!analysis) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ATS Resume Analysis Report - Agentic FuturePath AI</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 900px; margin: 0 auto; line-height: 1.6; }
          h1 { color: #1e40af; border-bottom: 3px solid #1e40af; padding-bottom: 15px; }
          h2 { color: #334155; margin-top: 30px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
          .score-box { background: linear-gradient(135deg, #1e3a5f, #0f2744); color: white; padding: 30px; border-radius: 12px; text-align: center; margin: 20px 0; }
          .score-box .score { font-size: 64px; font-weight: bold; }
          .score-box .label { font-size: 18px; opacity: 0.8; }
          .breakdown { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin: 20px 0; }
          .breakdown-item { background: #f1f5f9; padding: 15px; border-radius: 8px; }
          .breakdown-item .label { font-size: 14px; color: #C6D4E8; }
          .breakdown-item .score { font-size: 24px; font-weight: bold; color: #1e40af; }
          .keywords { display: flex; flex-wrap: wrap; gap: 8px; margin: 15px 0; }
          .keyword { padding: 4px 12px; border-radius: 20px; font-size: 12px; }
          .keyword.found { background: #d1fae5; color: #065f46; }
          .keyword.missing { background: #fee2e2; color: #991b1b; }
          .suggestion { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 15px; margin: 10px 0; }
          .suggestion.high { border-color: #ef4444; background: #fef2f2; }
          .suggestion.low { border-color: #3b82f6; background: #eff6ff; }
          .footer { margin-top: 40px; text-align: center; color: #C6D4E8; font-size: 12px; border-top: 1px solid #e2e8f0; padding-top: 20px; }
        </style>
      </head>
      <body>
        <h1>🎯 ATS Resume Analysis Report</h1>
        <p>Generated by Agentic FuturePath AI on ${new Date().toLocaleDateString()}</p>
        
        <div class="score-box">
          <div class="score" style="color: ${analysis.ats_score >= 80 ? '#10b981' : analysis.ats_score >= 60 ? '#f59e0b' : '#ef4444'}">${analysis.ats_score}%</div>
          <div class="label">Overall ATS Compatibility Score</div>
          <div style="margin-top: 10px; font-size: 14px;">
            Interview Likelihood: <strong>${analysis.summary?.interview_likelihood || 'N/A'}</strong>
          </div>
        </div>
        
        <h2>📊 Score Breakdown</h2>
        <div class="breakdown">
          <div class="breakdown-item">
            <div class="label">Keyword Match</div>
            <div class="score">${analysis.score_breakdown?.keyword_match || 0}%</div>
          </div>
          <div class="breakdown-item">
            <div class="label">Skills Match</div>
            <div class="score">${analysis.score_breakdown?.skills_match || 0}%</div>
          </div>
          <div class="breakdown-item">
            <div class="label">Experience Match</div>
            <div class="score">${analysis.score_breakdown?.experience_match || 0}%</div>
          </div>
          <div class="breakdown-item">
            <div class="label">Education Match</div>
            <div class="score">${analysis.score_breakdown?.education_match || 0}%</div>
          </div>
        </div>
        
        <h2>🔑 Keywords Analysis</h2>
        <h3>Found in Your Resume ✓</h3>
        <div class="keywords">
          ${(analysis.keywords_analysis?.found || []).map(k => `<span class="keyword found">${k}</span>`).join('')}
        </div>
        <h3>Missing Keywords ✗</h3>
        <div class="keywords">
          ${(analysis.keywords_analysis?.missing || []).map(k => `<span class="keyword missing">${k}</span>`).join('')}
        </div>
        
        <h2>💼 Skills Analysis</h2>
        <h3>Matched Skills</h3>
        <div class="keywords">
          ${(analysis.skills_analysis?.matched_skills || []).map(s => `<span class="keyword found">${s}</span>`).join('')}
        </div>
        <h3>Skills to Add</h3>
        <div class="keywords">
          ${(analysis.skills_analysis?.missing_skills || []).map(s => `<span class="keyword missing">${s}</span>`).join('')}
        </div>
        
        <h2>🚀 Key Improvements</h2>
        ${(analysis.improvement_suggestions || []).slice(0, 5).map(s => `
          <div class="suggestion ${s.priority?.toLowerCase() || 'medium'}">
            <strong>[${s.priority}] ${s.category}</strong><br>
            ${s.impact}<br>
            ${s.suggested ? `<em>Suggestion: ${s.suggested}</em>` : ''}
          </div>
        `).join('')}
        
        <h2>📝 Summary</h2>
        <p><strong>${analysis.summary?.overall_assessment || ''}</strong></p>
        <h3>Key Strengths</h3>
        <ul>
          ${(analysis.summary?.key_strengths || []).map(s => `<li>${s}</li>`).join('')}
        </ul>
        <h3>Critical Improvements</h3>
        <ul>
          ${(analysis.summary?.critical_improvements || []).map(s => `<li>${s}</li>`).join('')}
        </ul>
        
        <div class="footer">
          <p>Generated by Agentic FuturePath AI - Your Career & College Navigator</p>
          <p>This analysis is based on AI evaluation and should be used as a guide.</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };
  
  const startOver = () => {
    setStep(1);
    setResumeText('');
    setJobDescription('');
    setAnalysis(null);
  };


  return (
    <AppLayout>
      <PageHeader 
        title="Resume ATS Analyzer"
        description="Upload your resume and paste a job description to get an ATS compatibility score with actionable improvements"
      />
      
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 mb-8">
        {[
          { num: 1, label: 'Upload Resume' },
          { num: 2, label: 'Add Job Description' },
          { num: 3, label: 'View Analysis' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-blue-400' : 'text-slate-300'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                step >= s.num ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300'
              }`}>
                {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
              </div>
              <span className="text-sm hidden sm:inline">{s.label}</span>
            </div>
            {i < 2 && <ArrowRight className="w-4 h-4 text-slate-300 mx-2" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Upload Resume */}
      {step === 1 && (
        <div className="max-w-3xl mx-auto">
          <GlassCard>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Upload Your Resume</h2>
              <p className="text-slate-300">Upload a PDF file or paste your resume text below</p>
            </div>
            
            {/* Saved Resume Option */}
            {savedResume && (
              <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-white font-medium">Saved Resume Found</p>
                      <p className="text-sm text-slate-300">{savedResume.filename} - Uploaded {new Date(savedResume.uploaded_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <Button onClick={useSavedResume} className="bg-emerald-600 hover:bg-emerald-700">
                    Use This
                  </Button>
                </div>
              </div>
            )}
            
            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center hover:border-blue-500/50 transition-colors cursor-pointer mb-6"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-white font-medium mb-1">Drop your resume here or click to browse</p>
              <p className="text-slate-300 text-sm">Supports PDF and TXT files</p>
            </div>
            
            {/* Divider */}
            <div className="flex items-center gap-4 my-6">
              <div className="flex-1 h-px bg-slate-700"></div>
              <span className="text-slate-300 text-sm">OR</span>
              <div className="flex-1 h-px bg-slate-700"></div>
            </div>
            
            {/* Paste Resume */}
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Paste your resume text:</label>
              <Textarea
                value={resumeText}
                onChange={(e) => setResumeText(e.target.value)}
                placeholder="Paste your entire resume content here..."
                className="bg-slate-800 border-slate-700 text-white min-h-[200px]"
                data-testid="resume-text-input"
              />
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-slate-300">{resumeText.length} characters</span>
                <Button 
                  onClick={handlePasteResume}
                  disabled={resumeText.length < 100 || loading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="continue-btn"
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Continue'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </GlassCard>
        </div>
      )}
      
      {/* Step 2: Job Description */}
      {step === 2 && (
        <div className="max-w-3xl mx-auto">
          <GlassCard>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Paste the Job Description</h2>
              <p className="text-slate-300">Copy and paste the entire job posting you're applying to</p>
            </div>
            
            {/* Resume Summary */}
            <div className="mb-6 p-4 bg-slate-800/50 rounded-xl">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <div>
                  <p className="text-white font-medium">Resume Loaded</p>
                  <p className="text-sm text-slate-300">{resumeText.length} characters</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setStep(1)}
                  className="ml-auto text-slate-300"
                >
                  Change
                </Button>
              </div>
            </div>
            
            {/* Job Description Input */}
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Job Description:</label>
              <Textarea
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                placeholder="Paste the full job description here including responsibilities, requirements, qualifications, etc..."
                className="bg-slate-800 border-slate-700 text-white min-h-[300px]"
                data-testid="job-description-input"
              />
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-slate-300">{jobDescription.length} characters</span>
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="border-slate-700"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={analyzeResume}
                    disabled={jobDescription.length < 100 || loading}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="analyze-btn"
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span className="ml-2">Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Analyze Match
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      )}
      
      {/* Step 3: Results */}
      {step === 3 && analysis && (
        <div className="space-y-6">
          {/* Main Score Card */}
          <GlassCard>
            <div className="flex flex-col md:flex-row items-center gap-8">
              <ScoreCircle score={analysis.ats_score} />
              
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {analysis.ats_score >= 80 ? 'Excellent Match!' : 
                   analysis.ats_score >= 60 ? 'Good Match - Some Improvements Needed' :
                   analysis.ats_score >= 40 ? 'Fair Match - Significant Improvements Needed' :
                   'Needs Work - Major Changes Recommended'}
                </h2>
                <p className="text-slate-300 mb-4">{analysis.summary?.overall_assessment}</p>
                
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className={`px-4 py-2 rounded-lg border ${getScoreBgColor(analysis.ats_score)}`}>
                    <span className="text-sm text-slate-300">Interview Likelihood:</span>
                    <span className={`ml-2 font-semibold ${getScoreColor(analysis.ats_score)}`}>
                      {analysis.summary?.interview_likelihood || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                <Button onClick={downloadReport} className="bg-blue-600 hover:bg-blue-700">
                  <Download className="w-4 h-4 mr-2" />
                  Download Report
                </Button>
                <Button onClick={startOver} variant="outline" className="border-slate-700">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Analyze Another
                </Button>
              </div>
            </div>
          </GlassCard>
          
          {/* Score Breakdown */}
          <div className="grid md:grid-cols-2 gap-6">
            <GlassCard>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-400" />
                Score Breakdown
              </h3>
              <div className="space-y-4">
                <ScoreBar label="Keyword Match" score={analysis.score_breakdown?.keyword_match || 0} />
                <ScoreBar label="Skills Match" score={analysis.score_breakdown?.skills_match || 0} />
                <ScoreBar label="Experience Match" score={analysis.score_breakdown?.experience_match || 0} />
                <ScoreBar label="Education Match" score={analysis.score_breakdown?.education_match || 0} />
                <ScoreBar label="Formatting" score={analysis.score_breakdown?.formatting_score || 0} />
              </div>
            </GlassCard>
            
            {/* Key Strengths & Improvements */}
            <GlassCard>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                Key Insights
              </h3>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-emerald-400 mb-2">Strengths</h4>
                <ul className="space-y-1">
                  {(analysis.summary?.key_strengths || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-red-400 mb-2">Critical Improvements</h4>
                <ul className="space-y-1">
                  {(analysis.summary?.critical_improvements || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            </GlassCard>
          </div>
          
          {/* Keywords Analysis */}
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Keywords Analysis
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-emerald-400 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  Found ({(analysis.keywords_analysis?.found || []).length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(analysis.keywords_analysis?.found || []).map((k, i) => (
                    <KeywordChip key={i} keyword={k} found={true} />
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                  <XCircle className="w-4 h-4" />
                  Missing ({(analysis.keywords_analysis?.missing || []).length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(analysis.keywords_analysis?.missing || []).map((k, i) => (
                    <KeywordChip key={i} keyword={k} found={false} />
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Suggested Additions
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(analysis.keywords_analysis?.suggested_additions || []).map((k, i) => (
                    <span key={i} className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      + {k}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>
          
          {/* Skills Analysis */}
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-400" />
              Skills Analysis
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-emerald-400 mb-3">Matched Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {(analysis.skills_analysis?.matched_skills || []).map((s, i) => (
                    <span key={i} className="px-3 py-1 rounded-lg text-sm bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-red-400 mb-3">Missing Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {(analysis.skills_analysis?.missing_skills || []).map((s, i) => (
                    <span key={i} className="px-3 py-1 rounded-lg text-sm bg-red-500/20 text-red-400 border border-red-500/30">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-cyan-400 mb-3">Transferable Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {(analysis.skills_analysis?.transferable_skills || []).map((s, i) => (
                    <span key={i} className="px-3 py-1 rounded-lg text-sm bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </GlassCard>
          
          {/* Improvement Suggestions */}
          <GlassCard>
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              Actionable Improvements
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              {(analysis.improvement_suggestions || []).map((suggestion, i) => (
                <ImprovementCard key={i} suggestion={suggestion} />
              ))}
            </div>
          </GlassCard>

          {/* Bullet Rewrites */}
          {analysis.bullet_rewrites && analysis.bullet_rewrites.length > 0 && (
            <GlassCard data-testid="bullet-rewrites-section">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                Bullet Rewrites, ATS-Optimized
              </h3>
              <div className="space-y-4">
                {analysis.bullet_rewrites.map((b, i) => (
                  <div key={i} className="grid md:grid-cols-2 gap-3 p-4 rounded-lg bg-slate-900/40 border border-slate-700">
                    <div>
                      <p className="text-xs uppercase text-red-400 mb-1">Original</p>
                      <p className="text-sm text-slate-300">{b.original}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-emerald-400 mb-1">Improved</p>
                      <p className="text-sm text-white">{b.improved}</p>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Career Pivot Readiness */}
          {analysis.career_pivot_readiness && (
            <GlassCard data-testid="career-pivot-section">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-blue-400" />
                Career Pivot Readiness
              </h3>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="md:col-span-1 flex flex-col items-center justify-center p-4 rounded-lg bg-slate-900/40 border border-slate-700">
                  <ScoreCircle score={analysis.career_pivot_readiness.fit_score || 0} size="small" />
                  <p className="text-xs text-slate-300 mt-2">Pivot Fit Score</p>
                </div>
                <div className="md:col-span-2 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-xs uppercase text-blue-400 mb-1">Your Pivot Story</p>
                  <p className="text-sm text-white leading-relaxed">{analysis.career_pivot_readiness.pivot_story}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-emerald-400 mb-2">Strongest Transferable Skills</h4>
                  <ul className="space-y-1">
                    {(analysis.career_pivot_readiness.strongest_transferable_skills || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-amber-400 mb-2">Skills to Reframe</h4>
                  <ul className="space-y-1">
                    {(analysis.career_pivot_readiness.skills_to_reframe || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <RefreshCw className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-violet-400 mb-2">Undersold Experience</h4>
                  <ul className="space-y-1">
                    {(analysis.career_pivot_readiness.undersold_experience || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <Star className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </GlassCard>
          )}

          {/* Interview Readiness */}
          {analysis.interview_readiness && (
            <GlassCard data-testid="interview-readiness-section">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-cyan-400" />
                Interview Readiness
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <h4 className="text-sm font-medium text-cyan-400 mb-2">Likely Questions</h4>
                  <ol className="space-y-2 list-decimal list-inside text-sm text-slate-300">
                    {(analysis.interview_readiness.likely_questions || []).map((q, i) => (
                      <li key={i} className="leading-relaxed">{q}</li>
                    ))}
                  </ol>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-emerald-400 mb-2">Suggested Talking Points</h4>
                  <ul className="space-y-1">
                    {(analysis.interview_readiness.suggested_talking_points || []).map((p, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-red-400 mb-2">Weak Areas to Prepare</h4>
                  <ul className="space-y-1">
                    {(analysis.interview_readiness.weak_areas_to_prepare || []).map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                        {w}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </GlassCard>
          )}

          {/* Final Recommendations */}
          {analysis.final_recommendations && (
            <GlassCard data-testid="final-recommendations-section" className="bg-gradient-to-br from-blue-600/10 to-violet-600/10 border-blue-500/30">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-400" />
                Final Recommendations
              </h3>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="p-4 rounded-lg bg-slate-900/40 border border-slate-700">
                  <h4 className="text-xs uppercase text-emerald-400 mb-2">Top 3 Resume Improvements</h4>
                  <ol className="space-y-1 list-decimal list-inside text-sm text-slate-300">
                    {(analysis.final_recommendations.top_3_resume_improvements || []).slice(0, 3).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                </div>
                <div className="p-4 rounded-lg bg-slate-900/40 border border-slate-700">
                  <h4 className="text-xs uppercase text-blue-400 mb-2">Top 3 Keywords to Add</h4>
                  <ol className="space-y-1 list-decimal list-inside text-sm text-slate-300">
                    {(analysis.final_recommendations.top_3_keywords_to_add || []).slice(0, 3).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                </div>
                <div className="p-4 rounded-lg bg-slate-900/40 border border-slate-700">
                  <h4 className="text-xs uppercase text-violet-400 mb-2">Top 3 Skills to Strengthen</h4>
                  <ol className="space-y-1 list-decimal list-inside text-sm text-slate-300">
                    {(analysis.final_recommendations.top_3_skills_to_strengthen || []).slice(0, 3).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                </div>
              </div>
              {analysis.final_recommendations.best_next_move && (
                <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <p className="text-xs uppercase text-blue-400 mb-1">Best Next Move</p>
                  <p className="text-sm text-white leading-relaxed">{analysis.final_recommendations.best_next_move}</p>
                </div>
              )}
            </GlassCard>
          )}
        </div>
      )}
    </AppLayout>
  );
}
